import { DataPath } from '@mode-switch/express';
import { ExprCall, ExprForm, } from '@mode-switch/express';
import { Attr, Calc, Field, Model, Names, Values, } from '@mode-switch/model';
import { Compiler } from './Compiler';
export var Adapter;
(function (Adapter) {
    function compile(adapter, model, viewName, source, exprEnv) {
        return adapter.compile(model, viewName, source, exprEnv);
    }
    Adapter.compile = compile;
    let Fields;
    (function (Fields) {
        function make(adapter, model, viewName, source, type, opts = {}) {
            const alias = opts.alias || adapter.alias();
            switch (source) {
                case Names.Key:
                    return Names.Field.make(alias);
                case Values.Key:
                    return Values.Field.make(alias);
            }
            const result = compile(adapter, model, viewName, source);
            switch (result.type) {
                case "PARSE_FAILURE":
                case "RESOLVE_FAILURE":
                    throw new Error(`Could not compile expression ${source}: ${result.errors
                        .map((error) => error.message)
                        .join(', ')}`);
            }
            const { expr } = result;
            let role;
            let frame;
            switch (expr.stage) {
                case "CONST":
                case "SCALAR":
                    role = "DIMENSION";
                    break;
                case "AGGREGATE":
                    role = "MEASURE";
                    break;
                case "ANALYTIC":
                    role = "MEASURE";
                    frame = opts.frame;
                    break;
            }
            switch (role) {
                case "DIMENSION": {
                    const name = getDimensionName(expr);
                    const attr = Attr.Dimension.make(source, expr.data.type, expr.data.role);
                    switch (type) {
                        case "DISCRETE":
                            return Field.categorical(attr, name, alias);
                        case "CONTINUOUS":
                            return Field.ordinal(attr, name, alias, type, role);
                    }
                }
                case "MEASURE": {
                    const name = getMeasureName(adapter, model, viewName, expr, frame);
                    const attr = Attr.Measure.make(source, expr.data.type, expr.data.role);
                    switch (type) {
                        case "DISCRETE":
                            return Field.ordinal(attr, name, alias, type, role, frame);
                        case "CONTINUOUS":
                            return Field.quantitative(attr, name, alias, frame);
                    }
                }
                default:
                    throw new Error(`Cannot create field with role ${role}`);
            }
        }
        Fields.make = make;
        function getDimensionName(expr) {
            switch (expr.form) {
                case ExprForm.Bin:
                    return `Bin of ${DataPath.name(expr.path)}`;
                default: {
                    return expr.name;
                }
            }
        }
        function getMeasureName(adapter, model, viewName, expr, frame) {
            switch (expr.form) {
                case ExprForm.Access: {
                    const calculateUsing = expr.stage === "ANALYTIC"
                        ? getCalculateUsing(adapter, model, viewName, frame)
                        : '';
                    return `${DataPath.name(expr.path)}${calculateUsing}`;
                }
                case ExprForm.Aggregate: {
                    const name = DataPath.name(expr.path);
                    const attr = Model.Views.findAttr(model, viewName, name);
                    if (attr == null) {
                        return expr.name;
                    }
                    switch (attr.source) {
                        case "NAMES":
                        case "VALUES":
                            return attr.name;
                        default: {
                            let attrName = attr.name;
                            if (attr.defaults.aggregate !== expr.call) {
                                attrName = getCallName(attr.name, expr.call);
                            }
                            return attrName;
                        }
                    }
                }
                case ExprForm.Difference: {
                    const calcUsing = getCalculateUsing(adapter, model, viewName, frame);
                    return `Difference in ${getMeasureName(adapter, model, viewName, expr.expr, undefined)}${calcUsing}`;
                }
                case ExprForm.PctDifference: {
                    const calcUsing = getCalculateUsing(adapter, model, viewName, frame);
                    return `% Difference in ${getMeasureName(adapter, model, viewName, expr.expr, undefined)}${calcUsing}`;
                }
                case ExprForm.PctOfTotal: {
                    const calcUsing = getCalculateUsing(adapter, model, viewName, frame);
                    return `% of Total ${getMeasureName(adapter, model, viewName, expr.expr, undefined)}${calcUsing}`;
                }
            }
            return expr.name;
        }
        function getCallName(attrName, callName) {
            switch (callName.toUpperCase()) {
                case ExprCall.Name.Avg:
                    return `Avg. of ${attrName}`;
                case ExprCall.Name.Count:
                    return `Count of ${attrName}`;
                case ExprCall.Name.CountD:
                    return `Distinct Count of ${attrName}`;
                case ExprCall.Name.CountDA:
                    return `Approx. Distinct Count of ${attrName}`;
                case ExprCall.Name.Median:
                    return `Median of ${attrName}`;
                case ExprCall.Name.Min:
                    return `Min of ${attrName}`;
                case ExprCall.Name.Max:
                    return `Max of ${attrName}`;
                case ExprCall.Name.StDev:
                    return `Std. Dev. of ${attrName}`;
                case ExprCall.Name.StDevP:
                    return `Pop. Std. Dev. of ${attrName}`;
                case ExprCall.Name.Sum:
                    return `Sum of ${attrName}`;
                case ExprCall.Name.Var:
                    return `Variance of ${attrName}`;
                case ExprCall.Name.VarP:
                    return `Pop. Variance of ${attrName}`;
                default:
                    return attrName;
            }
        }
        function getCalculateUsing(adapter, model, viewName, frame) {
            return frame != null
                ? getFrameDescription(adapter, model, viewName, frame)
                : '';
        }
        function getFrameDescription(adapter, model, viewName, frame) {
            switch (frame.type) {
                case "TABLE":
                    return ` ${getFrameDirection(frame.address)} table`;
                case "PANE":
                    return ` ${getFrameDirection(frame.address)} pane`;
                case "CUSTOM": {
                    const names = getCustomFrameDimensionNames(adapter, model, viewName, frame);
                    if (names.length) {
                        return ` along ${names.join(', ')}`;
                    }
                    return ` along specific dimensions`;
                }
                case "CELL":
                    return ' for cell';
            }
        }
        function getFrameDirection(address) {
            switch (address) {
                case "ACROSS":
                    return 'across';
                case "ACROSS_DOWN":
                    return 'across down';
                case "DOWN":
                    return 'down';
                case "DOWN_ACROSS":
                    return 'down across';
                default:
                    return '';
            }
        }
        function getCustomFrameDimensionNames(adapter, model, viewName, frame) {
            const fields = frame.address.map((source) => make(adapter, model, viewName, source, "DISCRETE"));
            const someAreMeasures = fields.some((field) => field.role === "MEASURE");
            if (someAreMeasures) {
                throw new Error('Custom ExprFrame must only include dimensions');
            }
            return fields.map((field) => field.name);
        }
        function names(adapter, alias) {
            return Names.Field.make(alias || adapter.alias());
        }
        Fields.names = names;
        function values(adapter, alias) {
            return Values.Field.make(alias || adapter.alias());
        }
        Fields.values = values;
    })(Fields = Adapter.Fields || (Adapter.Fields = {}));
    let Calcs;
    (function (Calcs) {
        function make(adapter, model, viewName, name, source, type, desc) {
            const result = compile(adapter, model, viewName, source);
            switch (result.type) {
                case "PARSE_FAILURE":
                case "RESOLVE_FAILURE":
                    throw new Error(`Could not compile formula ${source}: ${result.errors
                        .map((error) => error.message)
                        .join(', ')}`);
            }
            const { expr } = result;
            let role;
            switch (expr.stage) {
                case "CONST":
                case "SCALAR":
                    role = "DIMENSION";
                    break;
                case "ANALYTIC":
                case "AGGREGATE":
                    role = "MEASURE";
                    break;
            }
            switch (role) {
                case "DIMENSION": {
                    const attr = Attr.Dimension.make(source, expr.data.type, expr.data.role);
                    switch (type) {
                        case "DISCRETE":
                            return Calc.categorical(attr, name, { desc });
                        case "CONTINUOUS":
                            return Calc.ordinal(attr, name, type, role, { desc });
                    }
                }
                case "MEASURE": {
                    const attr = Attr.Measure.make(source, expr.data.type, expr.data.role);
                    switch (type) {
                        case "DISCRETE":
                            return Calc.ordinal(attr, name, type, role, { desc });
                        case "CONTINUOUS":
                            return Calc.quantitative(attr, name, { desc });
                    }
                }
                default:
                    throw new Error(`Cannot create calculation with role ${role}`);
            }
        }
        Calcs.make = make;
    })(Calcs = Adapter.Calcs || (Adapter.Calcs = {}));
})(Adapter || (Adapter = {}));
